import AbstractShape, {Dimensions, DimensionProperties} from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import makerjs from "makerjs";
import {isEmpty} from "lodash";
import OffsetLine from "../Lines/OffsetLine";
import { IPoint } from "makerjs";

export class Arrow extends AbstractShape {
	constructor( dimensions?: object ) {
		super( 'arrow', dimensions );

		this.labelMappings = {
			head_width: 'head_height',
			head_length: 'head_width',
			stem_width: 'stem_height',
			stem_length: 'stem_width',
		};
	}

	getLength(): Length {
		return this.getStemLength().add( this.getHeadLength() );
	}

	getWidth(): Length {
		return this.getStemLength().add( this.getHeadLength() );
	}

	getHeight(): Length {
		return this.getStemWidth().max( this.getHeadWidth() );
	}

	/**
	 * Gets the stem width.
	 */
	getStemWidth(): Length {
		return this.cleanedPrimitives.stem_width;
	}

	/**
	 * Gets the stem length.
	 */
	getStemLength(): Length {
		return this.cleanedPrimitives.stem_length;
	}

	/**
	 * Gets the head width.
	 */
	getHeadWidth(): Length {
		return this.cleanedPrimitives.head_width;
	}

	/**
	 * Gets the head length.
	 */
	getHeadLength(): Length {
		return this.cleanedPrimitives.head_length;
	}

	/**
	 * Gets the difference in width between the head and stem..
	 */
	getWidthDifference(): Length {
		return this.getHeadWidth().subtract( this.getStemWidth() ).abs();
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'stem_width', secondary: 'stem_length', tertiary: 'head_length', quaternary: 'head_width' };
	}

	/**
	 * Creates a model instance of the shape.
	 */
	createModel(): void {
		if ( isEmpty( this.cleanedPrimitives ) ) {
			return;
		}

		super.createModel();

		makerjs.model.zero( this.model );
		makerjs.model.originate( this.model );
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {

		// Offset the center seeing as [ 0, 0 ] is the bottom-left corner of the stem.
		const centerY = this.getHeadWidth().divide( 2 ).subtract( this.getStemWidth().divide( 2 ) );

		if(focusedElement.includes("cutout_measurement") || forceRender) {
			return [
				new DimensionLine( [0, centerY.in_mm()], [0, centerY.add( this.getStemWidth() ).in_mm()], this.renderDimensionLabel( 'stem_height' ), [-40, 0], ratio, this.getDimensionLineType('measurements-arrow-stem_width', focusedElement, forceRender) ), // Stem height - vertical line
				new DimensionLine( [0, 0], [this.getStemLength().in_mm(), 0], this.renderDimensionLabel( 'stem_width' ), [0, -40], ratio, this.getDimensionLineType('measurements-arrow-stem_length', focusedElement, forceRender) ), // Stem width - Horizontal line

				// Head height - vertical line
				new DimensionLine(
					[this.getLength().in_mm(), 0],
					[this.getLength().in_mm(), this.getHeadWidth().in_mm()],
					this.renderDimensionLabel( 'head_height' ),
					[40, 0],
					ratio,
					this.getDimensionLineType('measurements-arrow-head_width', focusedElement, forceRender)
				),

				// Head width - horizontal line
				new DimensionLine(
					[this.getStemLength().in_mm(), 0],
					[this.getStemLength().add( this.getHeadLength() ).in_mm(), 0],
					this.renderDimensionLabel( 'head_width' ),
					[0, -40],
					ratio,
					this.getDimensionLineType('measurements-arrow-head_length', focusedElement, forceRender)
				),
			];
		}

		return [];
	}

	getDefaultDimensions(): Dimensions {
		return {
			stem_width: 10,
			stem_length: 50,
			head_length: 30,
			head_width: 40,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const parentWidth = enclosingRectangle.width();
		const parentHeight = enclosingRectangle.height();

		const width = parentWidth.divide(4).in_cm();
		const height = parentHeight.divide(4).in_cm();

		return {
			stem_width: width,
			stem_length: height,
			head_length: Math.round(width + (width / 4 )),
			head_width: Math.round(height + (width / 2 )),
		};
	}
}
